import type { OnlineStore, StorePermission } from '~/composables/useOnlineStores'

/**
 * Store membership / permission checks, kept out of `useOnlineStores` so
 * entry-level code (useApi → useStoreAdminAccess → useStorePreviewMode, the
 * store-permission middleware) does not pull the whole store editor into
 * every page's entry chunk. `useOnlineStores` re-exports these.
 */

export const STORE_PERMISSIONS: StorePermission[] = [
  'design',
  'inventory',
  'orders',
  'configuration',
  'publish',
  'messages',
  'financial',
]

/** True when the signed-in user owns this store (no owner-list fetch needed). */
export function isStoreOwner(
  store: Pick<OnlineStore, 'userId' | 'viewer'> | null | undefined,
  user: { id: number } | null | undefined,
): boolean {
  if (store?.viewer?.isOwner) return true
  return Boolean(user && store?.userId != null && user.id === store.userId)
}

/** True when the signed-in user is an owner or accepted collaborator. */
export function isStoreMember(
  store: Pick<OnlineStore, 'userId' | 'viewer'> | null | undefined,
  user: { id: number } | null | undefined,
): boolean {
  if (!user || !store) return false
  if (store.viewer != null) return true
  return isStoreOwner(store, user)
}

export function isStorePermission(value: unknown): value is StorePermission {
  return typeof value === 'string' && (STORE_PERMISSIONS as string[]).includes(value)
}

/** True when the viewer may perform a store admin action. Owners always pass. */
export function hasStorePermission(
  store: Pick<OnlineStore, 'userId' | 'viewer'> | null | undefined,
  user: { id: number } | null | undefined,
  permission: StorePermission,
): boolean {
  if (!user || !store) return false
  if (isStoreOwner(store, user)) return true
  return Boolean(store.viewer?.permissions?.includes(permission))
}
